import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { produce } from 'immer'

import { TimerStopwatch } from '@amzn/katal-metrics/lib/metricObject'
import { Button, ButtonVariant } from '@amzn/stencil-react-components/button'
import { Checkbox } from '@amzn/stencil-react-components/checkbox'
import { FlyoutContent, WithFlyout } from '@amzn/stencil-react-components/flyout'
import { useIsMounted } from '@amzn/stencil-react-components/hooks'
import { Input, InputWrapper, LabelPosition } from '@amzn/stencil-react-components/input'
import { Col, Container, Flex, Hr, View } from '@amzn/stencil-react-components/layout'
import { MessageBanner, MessageBannerType } from '@amzn/stencil-react-components/message-banner'
import { useBreakpoints } from '@amzn/stencil-react-components/responsive'
import { Spinner, SpinnerSize } from '@amzn/stencil-react-components/spinner'
import { SortDirection, TableColumn } from '@amzn/stencil-react-components/table'
import { H2, H4, Label } from '@amzn/stencil-react-components/text'
import {
    IconSearchSmall as IconSearch,
    IconSettingsSmall as IconSettings,
} from '@amzn/stencil-react-icons'

import { ModuleDisplayTable } from 'src/components/ModuleDisplayTable'
import { FILTER_DATA_IDS, ModuleListFilter } from 'src/components/ModuleListFilter'
import { ResponsiveRow } from 'src/components/ResponsiveRow'
import {
    CommonUserEventKeys,
    publishTimeSpentMetrics,
    publishUserEventMetrics,
    UserEventMethodNames,
} from 'src/metrics'
import { ModuleType } from 'src/models/dto/ModuleDTO'
import { ModuleStatus } from 'src/models/dto/ModuleStatus'
import {
    ModuleListFilters,
    ModuleListSortCriteria,
    ModuleSortField,
    PaginationConfig,
} from 'src/models/ListModulesRequest'
import { ModuleMetaData, SearchResultResponse } from 'src/models/SearchResultResponse'
import {
    fromParams,
    getDefaultModuleSearchQuery,
    toSearchParams,
} from 'src/pages/search/searchParams'
import { SearchService } from 'src/services/backend/SearchService'

export type {
    ModuleListFilters,
    ModuleListSortCriteria,
    ModuleSortField,
    PaginationConfig,
} from 'src/models/ListModulesRequest'

export interface ModuleSearchQuery {
    search: string
    owner: string
    startDate: string
    endDate: string
    lastModifiedBy: string
    searchArchived: boolean
    moduleStatus: ModuleStatus[]
    moduleTypes: ModuleType[]
    itemOffset: number
    numberOfItemsPerPage: number
    sortField: ModuleSortField
    sortDirection: SortDirection
}

export enum LAYOUT_DIRECTION {
    DESKTOP = 'desktop',
    TABLET = 'tablet',
    MOBILE = 'mobile',
}

export const useLayoutDirection = () => {
    const { matches } = useBreakpoints()

    if (matches.s) {
        return LAYOUT_DIRECTION.MOBILE
    }

    if (matches.m) {
        return LAYOUT_DIRECTION.TABLET
    }

    return LAYOUT_DIRECTION.DESKTOP
}

const pageTimer = new TimerStopwatch('SearchResultPageTimer')

interface SearchResultsPageInnerProps {
    searchQuery: ModuleSearchQuery
    setSearchQuery: React.Dispatch<ModuleSearchQuery>
    embedded?: boolean
    lockedModuleStatus?: ModuleStatus[]
    selectModuleAction?: TableColumn<ModuleMetaData>
    disabledFilters?: FILTER_DATA_IDS[]
    lockedModuleTypes?: ModuleType[]
    noTypeColumn?: boolean
}

export const SearchResultsPageInner = ({
    searchQuery,
    setSearchQuery,
    embedded,
    lockedModuleStatus,
    selectModuleAction,
    disabledFilters,
    noTypeColumn,
}: SearchResultsPageInnerProps) => {
    type SearchQuery = ModuleSearchQuery

    // nextSearchQuery is the work-in-progress search query
    // calling triggerSearch writes nextSearchQuery back into searchQuery
    const [nextSearchQuery, setNextSearchQuery] = useState<SearchQuery>(searchQuery)
    const [triggerSearchCounter, incrementCounter] = useReducer((x: number) => x + 1, 0)
    const [writeBack, setWriteBack] = useState(false)
    useEffect(() => {
        if (writeBack) {
            setSearchQuery(nextSearchQuery)
            incrementCounter()
            setWriteBack(false)
        }
    }, [writeBack, nextSearchQuery, setSearchQuery])
    const setSearchQueryAndTriggerSearch = useCallback(
        (update: React.SetStateAction<SearchQuery>) => {
            setNextSearchQuery(update)
            setWriteBack(true)
        },
        []
    )

    const {
        setSearch,
        setOwner,
        setStartDate,
        setEndDate,
        setLastModifiedBy,
        setModuleStatus,
        setModuleTypes,
        setNumberOfItemsPerPage,
    } = useMemo(
        () => ({
            setSearch: (value: string) =>
                setNextSearchQuery((prev) => ({ ...prev, search: value })),
            setOwner: (value: string) => setNextSearchQuery((prev) => ({ ...prev, owner: value })),
            setStartDate: (value: string) =>
                setNextSearchQuery((prev) => ({ ...prev, startDate: value })),
            setEndDate: (value: string) =>
                setNextSearchQuery((prev) => ({ ...prev, endDate: value })),
            setLastModifiedBy: (value: string) =>
                setNextSearchQuery((prev) => ({ ...prev, lastModifiedBy: value })),
            setModuleStatus: (value: ModuleStatus[]) =>
                setNextSearchQuery((prev) => ({ ...prev, moduleStatus: value })),
            setModuleTypes: (value: ModuleType[]) =>
                setNextSearchQuery((prev) => ({ ...prev, moduleTypes: value })),
            setNumberOfItemsPerPage: (value: number) =>
                setNextSearchQuery((prev) => ({ ...prev, numberOfItemsPerPage: value })),
        }),
        []
    )

    const [loadingResults, setLoadingResults] = useState<boolean>(false)
    const [searchResults, setSearchResults] = useState<SearchResultResponse | null>(null)
    const isMounted = useIsMounted()
    const callSearchAPI = useCallback(
        async (searchQuery1: SearchQuery, abort?: AbortController) => {
            setLoadingResults(true)
            const filters: ModuleListFilters = {
                owner: searchQuery1.owner,
                lastModifiedBy: searchQuery1.lastModifiedBy,
                startDate: searchQuery1.startDate,
                endDate: searchQuery1.endDate,
                moduleTypes: searchQuery1.moduleTypes,
                moduleStatus: searchQuery1.moduleStatus,
            }

            const pageConfig: PaginationConfig = {
                offset: searchQuery1.itemOffset,
                numberOfRecordsPerPage: searchQuery1.numberOfItemsPerPage,
            }

            const sortCriteria: ModuleListSortCriteria = {
                sortField: searchQuery1.sortField,
                sortDirection: searchQuery1.sortDirection,
            }

            try {
                const response: SearchResultResponse = await SearchService.searchModules(
                    searchQuery1.search,
                    filters,
                    pageConfig,
                    sortCriteria,
                    abort,
                    searchQuery1.searchArchived
                )

                if (!isMounted() || abort?.signal?.aborted) {
                    return
                }
                setSearchQuery(searchQuery1)
                setSearchResults(response)
            } finally {
                if (isMounted()) {
                    setLoadingResults(false)
                }
            }
        },
        [isMounted, setSearchQuery]
    )

    const resultTableRef = useRef<HTMLDivElement | null>(null)

    const handleSortCriteriaChange = useCallback(
        (
            sortField: ModuleSortField,
            sortDirection: SortDirection,
            offset: number,
            numberOfRecordsPerPage: number
        ) => {
            setSearchQueryAndTriggerSearch(
                produce((draft) => {
                    draft.sortField = sortField
                    draft.sortDirection = sortDirection
                    draft.itemOffset = offset
                    draft.numberOfItemsPerPage = numberOfRecordsPerPage
                })
            )
        },
        [setSearchQueryAndTriggerSearch]
    )

    const containerRef = useRef<HTMLDivElement | null>(null)
    useEffect(() => {
        containerRef.current?.focus()
    }, [])

    useEffect(() => {
        pageTimer.start()
        publishUserEventMetrics(UserEventMethodNames.SearchResult, CommonUserEventKeys.Open)

        return () => {
            pageTimer.stop()
            publishTimeSpentMetrics(UserEventMethodNames.SearchResult, pageTimer.value)
        }
    }, [])

    // Perform search
    useEffect(() => {
        const abort = new AbortController()
        void callSearchAPI(searchQuery, abort)
            .then(() => {
                resultTableRef.current?.focus()
            })
            .catch((e) => {
                const typeName = (e as Error).constructor.name
                if (typeName === 'Cancel' || typeName === 'CanceledError') {
                    return
                }
                console.error('search error', e)
            })
        return () => abort.abort()
    }, [triggerSearchCounter, callSearchAPI, searchQuery])

    const renderFlyout = useCallback(
        ({ close }: { close: () => void }) => (
            <FlyoutContent titleText='Filters' onCloseButtonClick={close}>
                <ModuleListFilter
                    owner={nextSearchQuery.owner}
                    lastModifiedBy={nextSearchQuery.lastModifiedBy}
                    startDate={nextSearchQuery.startDate}
                    endDate={nextSearchQuery.endDate}
                    moduleTypes={nextSearchQuery.moduleTypes}
                    moduleStatus={nextSearchQuery.moduleStatus}
                    setOwner={setOwner}
                    setLastModifiedBy={setLastModifiedBy}
                    setStartDate={setStartDate}
                    setEndDate={setEndDate}
                    setModuleTypes={setModuleTypes}
                    setModuleStatus={setModuleStatus}
                    lockedModuleStatus={lockedModuleStatus}
                    applyFilters={() => {
                        setSearchQueryAndTriggerSearch(
                            produce((draft) => {
                                draft.itemOffset = 0
                            })
                        )
                        close()
                    }}
                />
            </FlyoutContent>
        ),
        [
            nextSearchQuery.owner,
            nextSearchQuery.lastModifiedBy,
            nextSearchQuery.startDate,
            nextSearchQuery.endDate,
            nextSearchQuery.moduleTypes,
            nextSearchQuery.moduleStatus,
            setOwner,
            setLastModifiedBy,
            setStartDate,
            setEndDate,
            setModuleTypes,
            setModuleStatus,
            lockedModuleStatus,
            setSearchQueryAndTriggerSearch,
        ]
    )

    const onPageChange = useCallback(
        (offset: number, numberOfRecordsPerPage: number) => {
            setSearchQueryAndTriggerSearch(
                produce((draft) => {
                    draft.itemOffset = offset
                    draft.numberOfItemsPerPage = numberOfRecordsPerPage
                })
            )
        },
        [setSearchQueryAndTriggerSearch]
    )

    const sortCriteria = useMemo(
        () => ({
            sortField: nextSearchQuery.sortField,
            sortDirection: nextSearchQuery.sortDirection,
        }),
        [nextSearchQuery.sortDirection, nextSearchQuery.sortField]
    )

    const displaySearchResults = useMemo(() => {
        if (loadingResults || !searchResults) {
            return (
                <View display='flex' justifyContent='center' width='100%' alignItems='center'>
                    <Spinner size={SpinnerSize.Large} dataTestId={'module-search-data-spinner'} />
                </View>
            )
        }

        // No search results
        if ((searchResults.data?.length ?? 0) === 0) {
            return (
                <View display='flex' justifyContent='center' width='100%' alignItems='center'>
                    <H4 color='neutral70' textAlign='center' data-no-search-results>
                        {`No results found for "${nextSearchQuery.search}"`} <br />{' '}
                        {'Please search again.'}
                    </H4>
                </View>
            )
        }

        // Display search results
        return (
            <View flex='3 1 auto' tabIndex={-1} ref={resultTableRef}>
                <ModuleDisplayTable
                    searchResults={searchResults}
                    searchTerm={nextSearchQuery.search}
                    offset={nextSearchQuery.itemOffset}
                    onUpdateNumberOfItemsPerPage={setNumberOfItemsPerPage}
                    onPageChange={onPageChange}
                    onSortCriteriaChange={handleSortCriteriaChange}
                    sortCriteria={sortCriteria}
                    embeddedInModal={embedded}
                    selectModuleAction={selectModuleAction}
                    noTypeColumn={noTypeColumn}
                />
            </View>
        )
    }, [
        embedded,
        handleSortCriteriaChange,
        loadingResults,
        nextSearchQuery.itemOffset,
        nextSearchQuery.search,
        noTypeColumn,
        onPageChange,
        searchResults,
        selectModuleAction,
        setNumberOfItemsPerPage,
        sortCriteria,
    ])

    const applyFilters = useCallback(() => {
        setSearchQueryAndTriggerSearch(
            produce((draft) => {
                draft.itemOffset = 0
            })
        )
    }, [setSearchQueryAndTriggerSearch])

    const onSearchArchivedChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const checked = e.target.checked
            setSearchQueryAndTriggerSearch(
                produce((draft) => {
                    draft.itemOffset = 0
                    draft.searchArchived = checked
                })
            )
        },
        [setSearchQueryAndTriggerSearch]
    )

    const layout = useLayoutDirection()

    return (
        <Container
            ref={containerRef}
            backgroundColor='#F8F8F8'
            width='100%'
            minHeight='100vh'
            paddingHorizontal={0}
        >
            <Col gridGap='S300' padding='15px'>
                <Label htmlFor='module-search-input'>
                    <H2>Search</H2>
                </Label>
                <Flex
                    gridGap='16px'
                    flexDirection={layout === LAYOUT_DIRECTION.MOBILE ? 'column' : 'row'}
                >
                    {useMemo(
                        () => (
                            <Input
                                id='module-search-input'
                                key={'module-search-input'}
                                data-test-id='module-search-input'
                                name='Module Search Input'
                                width={layout === LAYOUT_DIRECTION.MOBILE ? '100%' : '50%'}
                                defaultValue={nextSearchQuery.search}
                                onChange={(e) => setSearch(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        setSearchQueryAndTriggerSearch(
                                            produce((draft) => {
                                                draft.itemOffset = 0
                                            })
                                        )
                                    }
                                }}
                                insetElementLeading={{
                                    element: <IconSearch aria-hidden={true} color='neutral60' />,
                                }}
                            />
                        ),
                        [layout, nextSearchQuery.search, setSearch, setSearchQueryAndTriggerSearch]
                    )}
                    <Button
                        variant={ButtonVariant.Primary}
                        onClick={() => {
                            setSearchQueryAndTriggerSearch(
                                produce((draft) => {
                                    draft.itemOffset = 0
                                })
                            )
                        }}
                        aria-label='Search'
                        data-test-id='search-button'
                    >
                        Search
                    </Button>
                </Flex>
                <ResponsiveRow>
                    {useMemo(
                        () => (
                            <InputWrapper
                                id='searched-archived-input'
                                labelText={'Search archived modules'}
                                labelPosition={LabelPosition.Trailing}
                            >
                                {(inputProps) => (
                                    <Checkbox
                                        {...inputProps}
                                        checked={nextSearchQuery.searchArchived}
                                        onChange={onSearchArchivedChange}
                                    />
                                )}
                            </InputWrapper>
                        ),
                        [nextSearchQuery.searchArchived, onSearchArchivedChange]
                    )}
                </ResponsiveRow>
                <Hr size='narrow' color='#B9C0C8' />
                {nextSearchQuery.searchArchived && (
                    <MessageBanner type={MessageBannerType.Informational}>
                        You are searching archived modules. Only archived modules will appear.
                    </MessageBanner>
                )}
                <Flex
                    gridGap='S300'
                    flexDirection={layout === LAYOUT_DIRECTION.MOBILE ? 'column' : 'row'}
                >
                    {useMemo(
                        () =>
                            layout === LAYOUT_DIRECTION.MOBILE ? (
                                <WithFlyout renderFlyout={renderFlyout}>
                                    {({ open }) => (
                                        <Button
                                            variant={ButtonVariant.Secondary}
                                            icon={<IconSettings aria-hidden={true} />}
                                            onClick={open}
                                            style={
                                                layout === LAYOUT_DIRECTION.MOBILE
                                                    ? { flex: '1 1 0' }
                                                    : { flex: '0 0 300px' }
                                            }
                                        >
                                            Filters
                                        </Button>
                                    )}
                                </WithFlyout>
                            ) : (
                                <ModuleListFilter
                                    title={'Filters'}
                                    owner={nextSearchQuery.owner}
                                    lastModifiedBy={nextSearchQuery.lastModifiedBy}
                                    startDate={nextSearchQuery.startDate}
                                    endDate={nextSearchQuery.endDate}
                                    moduleTypes={nextSearchQuery.moduleTypes}
                                    moduleStatus={nextSearchQuery.moduleStatus}
                                    setOwner={setOwner}
                                    setLastModifiedBy={setLastModifiedBy}
                                    setStartDate={setStartDate}
                                    setEndDate={setEndDate}
                                    setModuleTypes={setModuleTypes}
                                    setModuleStatus={setModuleStatus}
                                    applyFilters={applyFilters}
                                    lockedModuleStatus={lockedModuleStatus}
                                    disableFilters={disabledFilters}
                                />
                            ),
                        [
                            applyFilters,
                            disabledFilters,
                            layout,
                            lockedModuleStatus,
                            nextSearchQuery.endDate,
                            nextSearchQuery.lastModifiedBy,
                            nextSearchQuery.moduleStatus,
                            nextSearchQuery.moduleTypes,
                            nextSearchQuery.owner,
                            nextSearchQuery.startDate,
                            renderFlyout,
                            setEndDate,
                            setLastModifiedBy,
                            setModuleStatus,
                            setModuleTypes,
                            setOwner,
                            setStartDate,
                        ]
                    )}
                    {displaySearchResults}
                </Flex>
            </Col>
        </Container>
    )
}

export const SearchResultsPage = ({
    embeddedInModal: embedded,
    lockedModuleStatus,
    selectModuleAction,
    disabledFilters,
    lockedModuleTypes,
    noTypeColumn,
}: {
    embeddedInModal?: boolean
    lockedModuleStatus?: ModuleStatus[]
    selectModuleAction?: TableColumn<ModuleMetaData>
    disabledFilters?: FILTER_DATA_IDS[]
    lockedModuleTypes?: ModuleType[]
    noTypeColumn?: boolean
}) => {
    const [urlSearchParams, setUrlSearchParams] = useSearchParams()
    const urlSearchQuery = useMemo(() => fromParams(urlSearchParams), [urlSearchParams])
    const setUrlSearchQuery = useCallback(
        (updater: React.SetStateAction<ModuleSearchQuery>) => {
            const newQuery =
                typeof updater === 'function' ? updater(fromParams(urlSearchParams)) : updater
            setUrlSearchParams(toSearchParams(newQuery))
        },
        [urlSearchParams, setUrlSearchParams]
    )
    const [reactStateSearchQuery, setReactStateSearchQuery] = useState<ModuleSearchQuery>(() =>
        getDefaultModuleSearchQuery(lockedModuleStatus, lockedModuleTypes)
    )

    // If it's embedded in a modal, we use local state to switch between searches, otherwise we use URL query parameters
    const searchQuery: ModuleSearchQuery = embedded ? reactStateSearchQuery : urlSearchQuery
    const setSearchQuery: React.Dispatch<React.SetStateAction<ModuleSearchQuery>> = embedded
        ? setReactStateSearchQuery
        : setUrlSearchQuery

    return (
        <SearchResultsPageInner
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            embedded={embedded}
            lockedModuleStatus={lockedModuleStatus}
            selectModuleAction={selectModuleAction}
            disabledFilters={disabledFilters}
            lockedModuleTypes={lockedModuleTypes}
            noTypeColumn={noTypeColumn}
        />
    )
}
